import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { TranslateProvider } from "@/context/TranslateContext";
import { MyCinemaProvider } from "@/context/MyCinemaContext";
import { SearchProvider, useSearch } from "@/context/SearchContext";
import { AiProvider } from "@/context/AiContext";
import { AudioProvider } from "@/context/AudioContext";
import { GlobalAudioBar } from "@/components/GlobalAudioBar";
import { FloatingAskAI } from "@/components/FloatingAskAI";
import { AuthModal } from "@/components/AuthModal";
import { SearchOverlay } from "@/components/SearchOverlay";
import Home from "@/pages/Home";
import MovieDetail from "@/pages/MovieDetail";
import PersonDetail from "@/pages/PersonDetail";
import WhatToWatch from "@/pages/WhatToWatch";
import MyCinema from "@/pages/MyCinema";
import SongDetail from "@/pages/SongDetail";
import AlbumDetail from "@/pages/AlbumDetail";
import MoviesBrowse from "@/pages/MoviesBrowse";
import FilmLocations from "@/pages/FilmLocations";
import Charts from "@/pages/Charts";
import Television from "@/pages/Television";
import TVSerials from "@/pages/TVSerials";
import OTT from "@/pages/OTT";
import SeriesDetail from "@/pages/SeriesDetail";
import ChannelProfile from "@/pages/ChannelProfile";
import PlatformProfile from "@/pages/PlatformProfile";
import ReleaseCalendar from "@/pages/ReleaseCalendar";
import WatchGuide from "@/pages/WatchGuide";
import Article from "@/pages/Article";
import Explore from "@/pages/Explore";
import LandmarkSerial from "@/pages/LandmarkSerial";
import CommunityLibrary from "@/pages/CommunityLibrary";
import Contribute from "@/pages/Contribute";
import AdminContent from "@/pages/AdminContent";
import AdminUsers from "@/pages/AdminUsers";
import MalayalamHome from "@/pages/MalayalamHome";
import MalayalamSection from "@/pages/MalayalamSection";
import MalayalamGallery from "@/pages/MalayalamGallery";
import MalayalamArticle from "@/pages/MalayalamArticle";
import MalayalamAdmin from "@/pages/MalayalamAdmin";
import { MalayalamLangProvider } from "@/context/MalayalamLangContext";
import { RequireStaff } from "@/components/RequireStaff";
import { Toaster } from "sonner";

function MalayalamEditionRoot() {
  return (
    <MalayalamLangProvider>
      <Outlet />
    </MalayalamLangProvider>
  );
}

function GlobalOverlays() {
  const { open, setOpen } = useSearch();
  return (
    <>
      <AuthModal />
      <SearchOverlay open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true, lerp: 0.09 });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay" />
      <TranslateProvider>
        <MyCinemaProvider>
          <SearchProvider>
            <AiProvider>
            <AudioProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/movie/:id" element={<MovieDetail />} />
                <Route path="/person/:id" element={<PersonDetail />} />
                <Route path="/song/:id" element={<SongDetail />} />
                <Route path="/album/:id" element={<AlbumDetail />} />
                <Route path="/movies" element={<MoviesBrowse />} />
                <Route path="/locations" element={<FilmLocations />} />
                <Route path="/charts" element={<Charts />} />
                <Route path="/television" element={<Television />} />
                <Route path="/tv-serials" element={<TVSerials />} />
                <Route path="/ott" element={<OTT />} />
                <Route path="/series/:slug" element={<SeriesDetail />} />
                <Route path="/channels/:slug" element={<ChannelProfile />} />
                <Route path="/platforms/:slug" element={<PlatformProfile />} />
                <Route path="/release-calendar" element={<ReleaseCalendar />} />
                <Route path="/watch-guide" element={<WatchGuide />} />
                <Route path="/article/:slug" element={<Article />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/landmark/:slug" element={<LandmarkSerial />} />
                <Route path="/library" element={<CommunityLibrary />} />
                <Route path="/contribute" element={<Contribute />} />
                <Route path="/admin/content" element={<RequireStaff><AdminContent /></RequireStaff>} />
                <Route path="/admin/users" element={<RequireStaff><AdminUsers /></RequireStaff>} />
                <Route path="/watch" element={<WhatToWatch />} />
                <Route path="/my-cinema" element={<MyCinema />} />
                <Route path="/malayalam" element={<MalayalamEditionRoot />}>
                  <Route index element={<MalayalamHome />} />
                  <Route path="gallery" element={<MalayalamGallery />} />
                  <Route path=":section" element={<MalayalamSection />} />
                  <Route path=":section/:slug" element={<MalayalamArticle />} />
                </Route>
                <Route path="/admin/malayalam" element={<RequireStaff><MalayalamEditionRoot /></RequireStaff>}>
                  <Route index element={<MalayalamAdmin />} />
                </Route>
              </Routes>
              <GlobalOverlays />
              <FloatingAskAI />
            </BrowserRouter>
            <GlobalAudioBar />
            </AudioProvider>
            </AiProvider>
            <Toaster position="top-center" theme="light" richColors closeButton />
          </SearchProvider>
        </MyCinemaProvider>
      </TranslateProvider>
    </div>
  );
}

export default App;
